import { 
  Box, 
  Button, 
  Flex, 
  FormControl, 
  FormLabel, 
  HStack, 
  Icon, 
  IconButton,
  Input, 
  InputGroup, 
  InputRightElement, 
  Progress,
  Select, 
  Slider, 
  SliderFilledTrack, 
  SliderThumb, 
  SliderTrack, 
  Spinner, 
  Switch, 
  Text, 
  Tooltip, 
  VStack, 
  useColorModeValue,
  useToast,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Alert,
  AlertIcon,
  AlertDescription,
  Collapse
} from '@chakra-ui/react';
import { 
  FiCheck, 
  FiCloud, 
  FiEye, 
  FiEyeOff, 
  FiInfo, 
  FiPlay, 
  FiPause,
  FiDownload,
  FiUpload,
  FiRefreshCw,
  FiVolumeX 
} from 'react-icons/fi';
import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { uploadNarrationAudio, updateNarrationWithAudio } from '../../../scripts/unifiedNarrationSync';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/firebase';

// Methodist brand colors
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#7A288A';

interface ElevenLabsPanelProps {
  narrationText: string;
  phoneticText: string;
  narrationId: string | null;
  narrationTitle?: string;
  existingAudioUrl?: string;
  onAudioUploaded?: (audioUrl: string) => void;
}

interface Voice {
  voice_id: string;
  name: string;
  preview_url: string;
}

interface VoiceSelectProps {
  voices: Voice[];
  selectedVoice: string;
  onVoiceSelect: (voiceId: string) => void;
  isPlaying: string | null;
  onPlaySample: (voice: Voice) => void;
  onStopSample: () => void;
}

const ElevenLabsPanel: React.FC<ElevenLabsPanelProps> = ({
  narrationText,
  phoneticText,
  narrationId,
  narrationTitle,
  existingAudioUrl,
  onAudioUploaded
}) => {
  // Initialize state from localStorage with proper API key
  const [apiKey, setApiKey] = useState<string>(() => {
    const savedKey = localStorage.getItem('elevenLabsApiKey') || 'sk_5be4920418f4d0521ca9ec5368a00feadddd7e237da13167';
    if (savedKey) {
      // Verify the saved key on mount
      setTimeout(() => verifyApiKey(savedKey), 0);
      return savedKey;
    }
    return 'sk_5be4920418f4d0521ca9ec5368a00feadddd7e237da13167';
  });
  const [isApiKeyValid, setIsApiKeyValid] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  // Verify API key and fetch voices
  const verifyApiKey = useCallback(async (key: string) => {
    if (!key) {
      setIsApiKeyValid(false);
      return;
    }

    setIsValidating(true);
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/voices', {
        headers: {
          'Accept': 'application/json',
          'xi-api-key': key
        }
      });

      const isValid = response.ok;
      setIsApiKeyValid(isValid);
      
      if (isValid) {
        localStorage.setItem('elevenLabsApiKey', key);
        const data = await response.json();
        const voicesWithPreviews = data.voices.map((v: any) => ({
          voice_id: v.voice_id,
          name: v.name,
          preview_url: v.preview_url
        }));
        setVoices(voicesWithPreviews);
        
        // Set default voice if none selected
        if (!selectedVoice && voicesWithPreviews.length > 0) {
          setSelectedVoice(voicesWithPreviews[0].voice_id);
        }
      }
    } catch (error) {
      console.error('Error validating API key:', error);
      setIsApiKeyValid(false);
    } finally {
      setIsValidating(false);
    }
  }, []);

  // Handle API key change with debounce
  const handleApiKeyChange = useCallback(
    debounce((value: string) => {
      setApiKey(value);
      verifyApiKey(value);
    }, 500),
    [verifyApiKey]
  );

  // Initialize API key validation on mount
  useEffect(() => {
    const savedKey = localStorage.getItem('elevenLabsApiKey');
    if (savedKey) {
      setApiKey(savedKey);
      verifyApiKey(savedKey);
    }
  }, [verifyApiKey]);

  // Track verification status
  const [isVerifying, setIsVerifying] = useState(false);
  const [isKeyValid, setIsKeyValid] = useState<boolean | null>(null);

  // Initialize voice settings from localStorage with Methodist defaults
  const [selectedVoice, setSelectedVoice] = useState<string>(() => 
    localStorage.getItem('elevenLabsSelectedVoice') || 'ThT5KcBeYPX3keUQqHPh' // Default to Mark
  );
  
  // Voice settings optimized for healthcare narration
  const defaultStability = 0.85;
  const defaultClarity = 0.85;
  const defaultStyle = 0.35;
  const [stability, setStability] = useState<number>(() => 
    parseFloat(localStorage.getItem('elevenLabsStability') || defaultStability.toString()) // Higher stability for clear medical terms
  );
  
  const [clarity, setClarity] = useState<number>(() => 
    parseFloat(localStorage.getItem('elevenLabsClarity') || defaultClarity.toString()) // Higher clarity for professional sound
  );
  
  const [style, setStyle] = useState<number>(() => 
    parseFloat(localStorage.getItem('elevenLabsStyle') || defaultStyle.toString()) // Lower style for more natural delivery
  );

  // Add speed control
  const [speed, setSpeed] = useState<number>(() => 
    parseFloat(localStorage.getItem('elevenLabsSpeed') || '1.0') // Default speed
  );

  // State for voice selection and parameters
  const [voices, setVoices] = useState<Voice[]>([]);
  const [isPlayingSample, setIsPlayingSample] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // State for audio playback and generation
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isGeneratedAudio, setIsGeneratedAudio] = useState(false);
  
  // State for voice settings
  const [showSettings, setShowSettings] = useState(false);
  
  // State for audio generation
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [usage, setUsage] = useState<any>(null);
  
  // Token utilization counter
  const [tokenUtilization, setTokenUtilization] = useState<number>(0);
  
  // Toast for notifications
  const toast = useToast();
  
  // Background colors
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const inputBgColor = useColorModeValue('white', 'gray.600');
  
  // Calculate estimated tokens from text
  const estimateTokens = (text: string): number => {
    // Rough estimate: 1 token ≈ 4 characters
    return Math.ceil(text.length / 4);
  };

  // Update token utilization when text changes
  useEffect(() => {
    if (narrationText) {
      setTokenUtilization(estimateTokens(narrationText));
    }
  }, [narrationText]);

  // Format time helper
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle download
  const handleDownloadAudio = () => {
    if (currentAudioUrl) {
      const a = document.createElement('a');
      a.href = currentAudioUrl;
      a.download = `narration_${narrationId || 'audio'}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Setup audio event listeners
  const setupAudioListeners = useCallback(() => {
    if (!audioRef.current) return;
    
    // Set up audio event listeners
    audioRef.current.addEventListener('play', () => setIsPlaying(true));
    audioRef.current.addEventListener('pause', () => setIsPlaying(false));
    audioRef.current.addEventListener('ended', () => {
      setIsPlaying(false);
      setCurrentTime(0);
    });
    audioRef.current.addEventListener('timeupdate', () => {
      setCurrentTime(audioRef.current?.currentTime || 0);
    });
    audioRef.current.addEventListener('loadedmetadata', () => {
      setDuration(audioRef.current?.duration || 0);
    });
    audioRef.current.addEventListener('error', (e) => {
      console.error('Audio playback error:', e);
      setIsPlaying(false);
      toast({
        title: 'Playback Error',
        description: 'Could not play the audio file. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    });
  }, [toast]);

  // Initialize audio element on component mount
  useEffect(() => {
    // Initialize with an empty audio element
    if (!audioRef.current) {
      audioRef.current = new Audio();
      setupAudioListeners();
    }
    
    return () => {
      // Clean up on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, [setupAudioListeners]);

  // Clean up audio resources
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Add a cleanup effect for audio blob URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up any object URLs to prevent memory leaks
      if (currentAudioUrl && currentAudioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentAudioUrl);
      }
    };
  }, [currentAudioUrl]);

  // Handle URL updates with improved error handling for Firebase URLs
  useEffect(() => {
    const newUrl = audioUrl || existingAudioUrl;
    
    // Only update if we have a valid URL and narration is selected
    if (newUrl && narrationId && newUrl !== currentAudioUrl) {
      console.log('[AUDIO] Setting new audio URL:', newUrl);
      setCurrentAudioUrl(newUrl);
      
      // Handle Firebase Storage URLs which might need CORS handling
      const isFirebaseUrl = newUrl.includes('firebasestorage.googleapis.com');
      
      if (audioRef.current) {
        // Reset player state
        setIsPlaying(false);
        setCurrentTime(0);
        
        // Set new source
        audioRef.current.src = newUrl;
        audioRef.current.load();
        
        // For Firebase URLs, preload the audio to check for CORS issues
        if (isFirebaseUrl) {
          console.log('[AUDIO] Preloading Firebase audio URL');
          audioRef.current.load();
        }
      } else {
        // Create new audio element if it doesn't exist
        audioRef.current = new Audio(newUrl);
        setupAudioListeners();
      }
    } else if (!newUrl && currentAudioUrl) {
      // Clear audio when no URL is available
      setCurrentAudioUrl(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    }
  }, [audioUrl, existingAudioUrl, currentAudioUrl, narrationId, setupAudioListeners]);
  
  // Play/pause the audio with improved error handling
  const handlePlayPauseAudio = useCallback(() => {
    if (!audioRef.current || !currentAudioUrl) {
      toast({
        title: 'No Audio Available',
        description: 'Please select a narration or generate audio first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (isPlaying) {
      console.log('[AUDIO] Pausing audio');
      audioRef.current.pause();
    } else {
      console.log('[AUDIO] Attempting to play audio:', currentAudioUrl);
      
      // Ensure the audio source is set correctly
      if (!audioRef.current.src || !audioRef.current.src.includes(currentAudioUrl.replace('blob:', ''))) {
        console.log('[AUDIO] Updating audio source to:', currentAudioUrl);
        audioRef.current.src = currentAudioUrl;
        audioRef.current.load();
      }
      
      // Play the audio with proper error handling
      audioRef.current.play().catch(error => {
        console.error('[AUDIO] Error playing audio:', error);
        toast({
          title: 'Playback Error',
          description: 'Could not play the audio file. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
    }
  }, [isPlaying, currentAudioUrl, toast]);
  
  // Reset audio player
  const handleResetAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, []);

  // Fetch usage data from ElevenLabs
  const fetchUsage = async () => {
    if (!apiKey) return;
    
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/user/subscription', {
        method: 'GET',
        headers: {
          'xi-api-key': apiKey
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching usage: ${response.status}`);
      }
      
      const data = await response.json();
      setUsage(data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  // Fetch usage on component mount and when API key changes
  useEffect(() => {
    if (apiKey) {
      fetchUsage();
    }
  }, [apiKey]);

  // Handle voice selection change
  const handleVoiceChange = (voiceId: string) => {
    setSelectedVoice(voiceId);
    localStorage.setItem('elevenLabsSelectedVoice', voiceId);
  };

  // Voice settings configuration
  const voiceSettings = [
    {
      key: 'stability',
      label: 'Stability',
      description: 'Controls the consistency of voice output',
      color: methodistBlue,
      min: 0,
      max: 1
    },
    {
      key: 'speed',
      label: 'Speed',
      description: 'Adjust the speaking rate of the voice',
      color: methodistPurple,
      min: 0.5,
      max: 2.0
    },
    {
      key: 'similarity',
      label: 'Similarity',
      description: 'Controls how closely the voice matches the original',
      color: methodistGreen,
      min: 0,
      max: 1
    },
    {
      key: 'style',
      label: 'Style',
      description: 'Adjusts the expressiveness of the voice',
      color: methodistNavy,
      min: 0,
      max: 1
    }
  ];

  // Handle stability change
  const handleStabilityChange = (value: number) => {
    setStability(value);
    localStorage.setItem('elevenLabsStability', value.toString());
  };

  // Handle speed change
  const handleSpeedChange = (value: number) => {
    setSpeed(value);
    localStorage.setItem('elevenLabsSpeed', value.toString());
  };

  // Handle similarity change
  const handleSimilarityChange = (value: number) => {
    setClarity(value);
    localStorage.setItem('elevenLabsClarity', value.toString());
  };

  // Handle style change
  const handleStyleChange = (value: number) => {
    setStyle(value);
    localStorage.setItem('elevenLabsStyle', value.toString());
  };

  // Constants for ElevenLabs API
  const ENHANCED_MODEL = "eleven_turbo_v2";
  const DEFAULT_MODEL = "eleven_monolingual_v1";
  
  // Sample text for voice preview
  const SAMPLE_TEXT = "Hello! This is a sample of my voice.";
  
  // Voice selection component
  const VoiceSelect: React.FC<VoiceSelectProps> = ({ voices, selectedVoice, onVoiceSelect, isPlaying, onPlaySample, onStopSample }) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.400');

    return (
      <Box position="relative" width="100%">
        <FormControl>
          <FormLabel>Voice Selection</FormLabel>
          <Box 
            border="1px" 
            borderColor={borderColor} 
            borderRadius="md" 
            maxH="300px" 
            overflowY="auto"
            shadow="sm"
          >
            {voices.map((voice) => (
              <Flex
                key={voice.voice_id}
                p={4}
                alignItems="center"
                cursor="pointer"
                bg={selectedVoice === voice.voice_id ? `${methodistBlue}10` : bgColor}
                _hover={{ bg: selectedVoice === voice.voice_id ? `${methodistBlue}15` : hoverBg }}
                borderBottom="1px"
                borderColor={borderColor}
                onClick={() => onVoiceSelect(voice.voice_id)}
                transition="all 0.2s"
                position="relative"
              >
                <Box flex={1}>
                  <Text fontWeight={selectedVoice === voice.voice_id ? "bold" : "medium"} color={selectedVoice === voice.voice_id ? methodistBlue : undefined}>
                    {voice.name}
                  </Text>
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme="blue" variant="subtle">Enhanced v2</Badge>
                    <Text fontSize="xs" color={textColor}>
                      ID: {voice.voice_id.slice(0, 8)}...
                    </Text>
                  </HStack>
                </Box>
                {voice.preview_url && (
                  <Button
                    size="sm"
                    variant={selectedVoice === voice.voice_id ? "solid" : "outline"}
                    leftIcon={
                      isPlaying === voice.voice_id ? (
                        <Icon as={FiPause} />
                      ) : (
                        <Icon as={FiPlay} />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isPlaying === voice.voice_id) {
                        onStopSample();
                      } else {
                        onPlaySample(voice);
                      }
                    }}
                    colorScheme={isPlaying === voice.voice_id ? "red" : "blue"}
                    _hover={{
                      transform: "scale(1.02)",
                    }}
                    transition="all 0.2s"
                  >
                    {isPlaying === voice.voice_id ? "Stop" : "Preview"}
                  </Button>
                )}
              </Flex>
            ))}
          </Box>
        </FormControl>
      </Box>
    );
  };

  // State for currently playing voice
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlaySample = (voice: Voice) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    
    const audio = new Audio(voice.preview_url);
    previewAudioRef.current = audio;
    
    audio.play();
    setCurrentlyPlaying(voice.voice_id);
    
    audio.onended = () => {
      setCurrentlyPlaying(null);
    };
  };

  const handleStopSample = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
    }
    setCurrentlyPlaying(null);
  };

  // Handle audio generation with automatic Firebase upload
  const handleGenerateAudio = async () => {
    if (!apiKey || !selectedVoice || (!phoneticText && !narrationText)) {
      toast({
        title: "Missing Information",
        description: "Please ensure you have entered an API key, selected a voice, and provided text to generate.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!narrationId) {
      toast({
        title: "No Narration Selected",
        description: "Please select a narration before generating audio.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Always use phoneticText if available, fall back to narrationText if not
      const textToGenerate = phoneticText || narrationText;
      
      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${selectedVoice}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey
        },
        body: JSON.stringify({
          text: textToGenerate,
          model_id: ENHANCED_MODEL,
          voice_settings: {
            stability,
            similarity_boost: clarity,
            style,
            speed
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate audio');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      setCurrentAudioUrl(audioUrl);
      setIsGeneratedAudio(true);

      // Reset audio player state
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      setCurrentTime(0);

      toast({
        title: "Audio Generated",
        description: "Your audio has been generated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Automatically upload to Firebase
      if (narrationId) {
        setIsUploading(true);
        try {
          // Continue using audio/slides path structure until migration is complete
          const uploadType = narrationId.startsWith('dashboard-') ? 'dashboard' : 'slide';
          const uploadUrl = await uploadNarrationAudio(audioBlob, narrationId, uploadType);
          
          if (onAudioUploaded) {
            onAudioUploaded(uploadUrl);
          }

          toast({
            title: 'Upload Successful',
            description: 'Audio saved to Firebase',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } catch (uploadError) {
          console.error('[AUDIO] Error uploading to Firebase:', uploadError);
          toast({
            title: 'Upload Failed',
            description: uploadError instanceof Error ? uploadError.message : 'Upload failed',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsUploading(false);
        }
      }

    } catch (error) {
      console.error('Error generating audio:', error);
      toast({
        title: "Generation Failed",
        description: "Failed to generate audio. Please check your API key and try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manual Firebase upload with proper event typing
  const handleUploadClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!audioUrl) return;
    
    try {
      const audioBlob = await fetch(audioUrl).then(r => r.blob());
      await handleUploadToFirebase(audioBlob);
    } catch (error) {
      console.error('Error preparing audio for upload:', error);
      toast({
        title: 'Upload Failed',
        description: 'Could not prepare audio for upload',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Core upload logic separated from event handler
  const handleUploadToFirebase = async (audioBlob: Blob) => {
    if (!narrationId) return;
    
    setIsUploading(true);
    try {
      // Continue using audio/slides path structure until migration is complete
      const uploadType = narrationId.startsWith('dashboard-') ? 'dashboard' : 'slide';
      const uploadUrl = await uploadNarrationAudio(audioBlob, narrationId, uploadType);
      
      if (onAudioUploaded) {
        onAudioUploaded(uploadUrl);
      }

      toast({
        title: 'Upload Successful',
        description: 'Audio saved to Firebase',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('[AUDIO] Error uploading to Firebase:', error);
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'Upload failed',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  return (
    <Box 
      borderWidth="1px" 
      borderRadius="md" 
      p={4} 
      bg={bgColor}
      height="100%"
      overflowY="auto"
    >
      <VStack spacing={4} align="stretch">
        {/* Header with API Status */}
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight="bold" color={methodistNavy}>ElevenLabs TTS</Text>
          <HStack spacing={2}>
            <Badge colorScheme="purple">
              ~{tokenUtilization} tokens
            </Badge>
            {usage && (
              <Badge colorScheme={usage.character_count < usage.character_limit * 0.8 ? "green" : "orange"}>
                {Math.round((usage.character_count / usage.character_limit) * 100)}% Used
              </Badge>
            )}
          </HStack>
        </Flex>

        {/* Narration Info Card */}
        <Card variant="outline" bg={useColorModeValue('white', 'gray.700')} mb={2}>
          <CardHeader pb={2}>
            <Flex justifyContent="space-between" alignItems="center">
              <Heading size="sm" color={methodistNavy}>
                {narrationTitle || (narrationId ? `Narration ${narrationId}` : 'No Narration Selected')}
              </Heading>
              {narrationId && (
                <Badge colorScheme={existingAudioUrl ? "green" : "yellow"}>
                  {existingAudioUrl ? "Uploaded" : "Pending"}
                </Badge>
              )}
            </Flex>
          </CardHeader>
          <CardBody pt={0}>
            {!narrationId ? (
              <Alert status="info" variant="subtle" borderRadius="md">
                <AlertIcon as={FiInfo} />
                <AlertDescription fontSize="sm">Select a narration from the list to generate or edit audio.</AlertDescription>
              </Alert>
            ) : (
              <Box>
                <Text fontSize="sm" noOfLines={2} color="gray.600" mb={2}>
                  {narrationText ? narrationText.substring(0, 100) + (narrationText.length > 100 ? '...' : '') : 'No text available'}
                </Text>
                {currentAudioUrl ? (
                  <Box borderWidth="1px" borderRadius="md" p={3} bg={useColorModeValue('gray.50', 'gray.800')}>
                    <VStack spacing={2} align="stretch">
                      <Flex justify="space-between" align="center">
                        <HStack>
                          <IconButton
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            icon={<Icon as={isPlaying ? FiPause : FiPlay} />}
                            onClick={handlePlayPauseAudio}
                            colorScheme="blue"
                            size="sm"
                            variant="solid"
                            bg={methodistBlue}
                            _hover={{ bg: 'blue.600' }}
                          />
                          <IconButton
                            aria-label="Reset"
                            icon={<Icon as={FiRefreshCw} />}
                            onClick={handleResetAudio}
                            size="sm"
                            variant="ghost"
                            isDisabled={!currentAudioUrl || currentTime === 0}
                          />
                        </HStack>
                        <Badge colorScheme="green">READY</Badge>
                      </Flex>
                      <HStack spacing={4}>
                        <Slider
                          value={currentTime}
                          min={0}
                          max={duration || 100}
                          onChange={(val) => {
                            if (audioRef.current) {
                              audioRef.current.currentTime = val;
                              setCurrentTime(val);
                            }
                          }}
                          flex="1"
                          isDisabled={!currentAudioUrl}
                        >
                          <SliderTrack bg="gray.200">
                            <SliderFilledTrack bg={methodistBlue} />
                          </SliderTrack>
                          <SliderThumb boxSize={3} />
                        </Slider>
                        <Text fontSize="sm" color="gray.500" minW="70px" textAlign="right">
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </Text>
                      </HStack>
                      <HStack justify="flex-end" spacing={2}>
                        <Tooltip label="Download Audio File">
                          <IconButton
                            aria-label="Download"
                            icon={<Icon as={FiDownload} />}
                            onClick={handleDownloadAudio}
                            variant="ghost"
                            colorScheme="blue"
                            size="sm"
                            isDisabled={!currentAudioUrl}
                          />
                        </Tooltip>
                        {/* Only show manual upload button for existing generated audio that hasn't been uploaded yet */}
                        {existingAudioUrl !== currentAudioUrl && narrationId && isGeneratedAudio && !isUploading && (
                          <Tooltip label="Save to Firebase Storage">
                            <IconButton
                              aria-label="Save to Firebase"
                              icon={<Icon as={FiUpload} />}
                              onClick={handleUploadClick}
                              variant="ghost"
                              colorScheme="green"
                              size="sm"
                              isLoading={isUploading}
                              isDisabled={!currentAudioUrl || isUploading}
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </VStack>
                  </Box>
                ) : (
                  <Alert status="warning" variant="subtle" borderRadius="md">
                    <AlertIcon as={FiVolumeX} />
                    <AlertDescription fontSize="sm">
                      {existingAudioUrl ? "Loading audio..." : "No audio available. Generate audio using the button below."}
                    </AlertDescription>
                  </Alert>
                )}
              </Box>
            )}
          </CardBody>
        </Card>

        {/* API Key Input */}
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>
            <HStack spacing={2}>
              <Text color={methodistNavy}>API Key</Text>
              {isApiKeyValid && (
                <Badge colorScheme="green" variant="subtle">
                  Verified
                </Badge>
              )}
            </HStack>
          </FormLabel>
          <InputGroup size="sm">
            <Input
              pr="4.5rem"
              type={showApiKey ? "text" : "password"}
              placeholder="Enter API key"
              value={apiKey}
              onChange={(e) => handleApiKeyChange(e.target.value)}
              isInvalid={apiKey !== '' && !isApiKeyValid}
              disabled={isValidating}
            />
            <InputRightElement width="4.5rem">
              <HStack spacing={1}>
                {isValidating && <Spinner size="sm" />}
                {isApiKeyValid && <Icon as={FiCheck} color="green.500" />}
                <IconButton
                  aria-label={showApiKey ? "Hide API key" : "Show API key"}
                  icon={showApiKey ? <FiEyeOff /> : <FiEye />}
                  onClick={() => setShowApiKey(!showApiKey)}
                />
              </HStack>
            </InputRightElement>
          </InputGroup>
          {apiKey !== '' && !isApiKeyValid && (
            <Text fontSize="xs" color="red.500" mt={1}>
              Invalid API key. Please check and try again.
            </Text>
          )}
          {usage && (
            <Box mt={1}>
              <Text fontSize="xs" color="gray.600">
                Characters: {usage.character_count.toLocaleString()} / {usage.character_limit.toLocaleString()}
              </Text>
              <Progress 
                size="xs" 
                value={(usage.character_count / usage.character_limit) * 100}
                colorScheme={usage.character_count < usage.character_limit * 0.8 ? "green" : "orange"}
              />
            </Box>
          )}
        </FormControl>

        {/* Voice Selection */}
        <VoiceSelect
          voices={voices}
          selectedVoice={selectedVoice}
          onVoiceSelect={handleVoiceChange}
          isPlaying={currentlyPlaying}
          onPlaySample={handlePlaySample}
          onStopSample={handleStopSample}
        />

        {/* Voice Settings */}
        <Box>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" fontWeight="medium" color={methodistNavy}>Voice Settings</Text>
            <IconButton
              aria-label="Toggle settings"
              icon={<Icon as={showSettings ? FiEyeOff : FiEye} />}
              size="sm"
              variant="ghost"
              onClick={() => setShowSettings(!showSettings)}
              color={methodistNavy}
              isDisabled={!narrationId || !isApiKeyValid}
            />
          </HStack>
          <Collapse in={showSettings}>
            <VStack spacing={4} align="stretch">
              {voiceSettings.map((setting) => (
                <FormControl key={setting.key} isDisabled={!narrationId || !isApiKeyValid}>
                  <Flex justify="space-between" align="center" mb={1}>
                    <FormLabel margin={0}>{setting.label}</FormLabel>
                    <Tooltip label={setting.description}>
                      <Icon as={FiInfo} color="gray.500" />
                    </Tooltip>
                  </Flex>
                  <Flex align="center">
                    <Slider
                      flex="1"
                      value={
                        setting.key === 'stability' ? stability :
                        setting.key === 'speed' ? speed :
                        setting.key === 'similarity' ? clarity :
                        setting.key === 'style' ? style : 0
                      }
                      onChange={(val) => {
                        if (setting.key === 'stability') setStability(val);
                        else if (setting.key === 'speed') setSpeed(val);
                        else if (setting.key === 'similarity') setClarity(val);
                        else if (setting.key === 'style') setStyle(val);
                        localStorage.setItem(`elevenLabs${setting.key.charAt(0).toUpperCase() + setting.key.slice(1)}`, val.toString());
                      }}
                      min={setting.min}
                      max={setting.max}
                      step={0.01}
                    >
                      <SliderTrack bg="gray.200">
                        <SliderFilledTrack bg={setting.color} />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <Text ml={2} minW="40px" textAlign="right">
                      {setting.key === 'speed' 
                        ? speed.toFixed(1) + 'x'
                        : Math.round(
                            setting.key === 'stability' ? stability * 100 :
                            setting.key === 'similarity' ? clarity * 100 :
                            setting.key === 'style' ? style * 100 : 0
                          ) + '%'
                      }
                    </Text>
                  </Flex>
                </FormControl>
              ))}
            </VStack>
          </Collapse>
        </Box>

        {/* Generate Button */}
        <Button
          onClick={handleGenerateAudio}
          isLoading={isLoading}
          loadingText="Generating..."
          leftIcon={<Icon as={FiCloud} />}
          isDisabled={!apiKey || !selectedVoice || (!phoneticText && !narrationText) || isValidating || !narrationId || !isApiKeyValid}
          bg={methodistBlue}
          color="white"
          _hover={{ bg: 'blue.600' }}
          _active={{ bg: 'blue.700' }}
          height="44px"
          fontSize="md"
          fontWeight="600"
        >
          Generate Audio
        </Button>
      </VStack>
    </Box>
  );
};

export default ElevenLabsPanel;
